import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  ClockOutlined,
  DiscountUniversal,
  MapOutlined,
  UserGroupOutlined,
} from '@duxton-web/icons';
import { Button, Divider, Form, Modal, Tooltip } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import { useLocalStorage } from 'react-use';

import FullDivider from '../../../common/FullDivider';
import AdvancedConfig from './AdvancedConfig';
import AssignmentStrategyInputCarousel from './AssignmentStrategyInputCarousel';
import AssignmentStrategyOption, {
  getTimeSliceStrategyTitle,
} from './AssignmentStrategyOption';
import TimeShuffle from './TimeShuffle';
import { newCreationFlowEvents } from '@/common/Scribe/EventsList';
import Service, { Experiment } from '@/lib/App';
import AllocationCalendar from '@/pages/ExperimentFormV2/common/AllocationCalendar';
import {
  useTreatments,
  useVariables,
} from '@/pages/ExperimentFormV2/contexts/ExperimentFormInfoProvider';
import {
  BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS,
  CustomTimeSliceStrategy,
  ExperimentAssignmentStrategy,
  ExpFormField,
  SELECTED_EXPERIMENT_LS_KEY,
} from '@/pages/ExperimentFormV2/helper/consts';

interface AssignmentStrategyProps {
  isViewMode?: boolean;
}

const extraTimeSliceMenu = [
  {
    key: ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
    title: 'Balanced Shuffle (Recommended)',
    subtitle: 'Ensure equally balanced representation of all treatments.',
  },
  {
    key: ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    title: 'Sequential (Legacy)',
    subtitle:
      'The sequence will follow the treatments (Control, A, B, Control, ...)',
  },
];

export function renderAssignedStrategyOption(strategy: string) {
  let metadata = { icon: null, title: '', subtitle: '' };

  switch (strategy) {
    case ExperimentAssignmentStrategy.UNIFORM_CHOICE:
    case ExperimentAssignmentStrategy.WEIGHTED_CHOICE:
      metadata = {
        icon: <DiscountUniversal />,
        title: 'Percentage Split',
        subtitle: 'Each bucket has a same or different weight.',
      };
      break;
    case ExperimentAssignmentStrategy.TIME_SLICE:
    case ExperimentAssignmentStrategy.TIME_SLICE_V2:
    case ExperimentAssignmentStrategy.TIME_SLICE_GROUPED:
    case ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE:
    case ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE:
      metadata = {
        icon: <ClockOutlined />,
        title: getTimeSliceStrategyTitle(strategy),
        subtitle: 'Specify a period of time for each variant.',
      };
      break;
    case ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE:
      metadata = {
        icon: <MapOutlined />,
        title: 'Geo Time Slice Shuffle',
        subtitle: 'Shuffle based on geo regions',
      };
      break;
    case ExperimentAssignmentStrategy.EXPLICIT_GROUP:
      metadata = {
        icon: <UserGroupOutlined />,
        title: 'Explicit Group',
        subtitle: 'Specify a group for each variant.',
      };
      break;
    default:
      metadata = {
        icon: <UserGroupOutlined />,
        title: 'Unsupported assignment strategy',
        subtitle: 'Unsupported assignment strategy',
      };
  }

  return (
    <AssignmentStrategyOption
      disabled
      title={metadata.title}
      subtitle={metadata.subtitle}
      selected={[...Object.values(ExperimentAssignmentStrategy)].includes(
        strategy,
      )}
      icon={metadata.icon}
    />
  );
}

const AssignmentStrategy: FC<AssignmentStrategyProps> = ({
  isViewMode = false,
}) => {
  const form = Form.useFormInstance();
  const currentDomain = Form.useWatch(ExpFormField.DOMAIN, {
    form,
    preserve: true,
  });
  const currentStrategyName = Form.useWatch(ExpFormField.STRATEGY.NAME, {
    form,
    preserve: true,
  });

  const customTimeSliceStrategyDuration = Form.useWatch(
    ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DURATION,
    {
      form,
      preserve: true,
    },
  );
  const customTimeSliceStrategyDurationHourRemainder =
    customTimeSliceStrategyDuration % (60 * 60);
  const blockCountEachHour =
    customTimeSliceStrategyDurationHourRemainder === 0
      ? 1
      : customTimeSliceStrategyDurationHourRemainder === 30 * 60
        ? 2
        : customTimeSliceStrategyDurationHourRemainder === 15 * 60 || 45 * 60
          ? 4
          : 1;
  const startTime = Form.useWatch(ExpFormField.START, { form, preserve: true });
  const endTime = Form.useWatch(ExpFormField.END, { form, preserve: true });

  const [selectedExperiment] = useLocalStorage<Experiment>(
    SELECTED_EXPERIMENT_LS_KEY,
  );

  const [treatments, setTreatments] = useTreatments();
  const [variables] = useVariables();

  const [timeSliceType, setTimeSliceType] = useState<string>('');

  useEffect(() => {
    if (currentDomain) {
      setTimeSliceType('');
    }
  }, [currentDomain]);

  useEffect(() => {
    if (
      !timeSliceType &&
      currentStrategyName &&
      [
        ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
        ExperimentAssignmentStrategy.TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
        ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
      ].includes(currentStrategyName)
    ) {
      setTimeSliceType(currentStrategyName);
    }
  }, [timeSliceType, currentStrategyName]);

  const availableStrategies = useMemo(() => {
    let strategies = Service.cache.getAllStrategies();
    const domainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
    if (currentDomain) {
      if (!domainlessFeatureFlag) {
        strategies = strategies.filter(
          (strategy) =>
            strategy.allowedDomains &&
            strategy.allowedDomains.includes(currentDomain),
        );
      }
    }
    return strategies;
  }, [currentDomain]);

  const currentDomainMetadata = useMemo(() => {
    const allDomainMetadata = Service?.cache?.getAllDomainMetadata();
    const result = allDomainMetadata?.find(
      (metadata) => metadata.name === currentDomain,
    );
    return result;
  }, [currentDomain]);

  useEffect(() => {
    if (
      currentDomainMetadata?.strategies?.includes(
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
      ) &&
      currentStrategyName === ExperimentAssignmentStrategy.TIME_SLICE
    ) {
      form.setFieldValue(
        ExpFormField.STRATEGY.NAME,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
      );
      setTimeSliceType(ExperimentAssignmentStrategy.TIME_SLICE_V2);
    }
  }, [currentDomainMetadata, currentStrategyName]);

  const availableStrategieNames = availableStrategies.map(
    (strategy) => strategy.name,
  );

  useEffect(() => {
    if (
      currentStrategyName === ExperimentAssignmentStrategy.UNIFORM_CHOICE &&
      treatments.length > 0
    ) {
      treatments.forEach((treatment) => {
        form.setFieldValue(
          ExpFormField.STRATEGY.TREATMENT_VALUE(treatment.id),
          Math.round(100 / treatments.length),
        );
      });
    }
  }, [treatments.length, currentStrategyName]);

  const handleToggleTimeShuffle = (checked: boolean) => {
    if (
      [
        ExperimentAssignmentStrategy.TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
        ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
      ].includes(currentStrategyName)
    ) {
      if (checked) {
        form.setFieldValue(
          ExpFormField.STRATEGY.NAME,
          ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
        );
      } else {
        if (
          currentDomainMetadata?.strategies?.includes(
            ExperimentAssignmentStrategy.TIME_SLICE_V2,
          )
        ) {
          form.setFieldValue(
            ExpFormField.STRATEGY.NAME,
            ExperimentAssignmentStrategy.TIME_SLICE_V2,
          );
        } else {
          form.setFieldValue(
            ExpFormField.STRATEGY.NAME,
            ExperimentAssignmentStrategy.TIME_SLICE,
          );
        }
      }
    }
  };

  const handleSelectStrategy = (strategyToSelect: string) => {
    scribe.sendEvent(newCreationFlowEvents.selectAssignmentStrategy.name, {
      experiment_name: form.getFieldValue(ExpFormField.NAME),
      assignment_strategy_selected: strategyToSelect,
      STATE_NAME: newCreationFlowEvents.selectAssignmentStrategy.state,
    });
    form.resetFields([ExpFormField.STRATEGY.ROOT]);
    form.setFieldValue(ExpFormField.STRATEGY.NAME, strategyToSelect);

    if (strategyToSelect === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE) {
      form.setFieldValue(
        ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_NAME,
        CustomTimeSliceStrategy.BALANCED_SHUFFLE,
      );
    }
  };

  const showBalancedShuffleTimeSliceSwitchWarning = (strategy: string) => {
    Modal.confirm({
      title: 'You will lose part of your setup!',
      content: `Balanced shuffle time slice only allow up to ${BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS} treatments. First ${BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS} treatments will be preserved, others would be deleted. Do you want to continue?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        setTreatments((treatments) =>
          treatments.slice(0, BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS),
        );
        handleSelectStrategy(strategy);
        setTimeSliceType(strategy);
        return;
      },
      onCancel() {
        return;
      },
    });
  };

  return (
    <>
      <Divider
        orientation="left"
        orientationMargin="0"
        style={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 700 }}
      >
        Assignment Strategy
      </Divider>
      {isViewMode ? (
        renderAssignedStrategyOption(currentStrategyName)
      ) : (
        <Tooltip
          title={
            variables.length === 0 ? 'You need to add a variable first' : ''
          }
          placement="bottom"
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
            }}
          >
            {availableStrategieNames.some((strategyName) =>
              [
                ExperimentAssignmentStrategy.UNIFORM_CHOICE,
                ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
              ].includes(strategyName),
            ) && (
              <AssignmentStrategyOption
                icon={<DiscountUniversal />}
                title="Percentage Split"
                subtitle="Each bucket has a same or different weight."
                selected={[
                  ExperimentAssignmentStrategy.UNIFORM_CHOICE,
                  ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
                ].includes(currentStrategyName)}
                onSelect={() => {
                  handleSelectStrategy(
                    ExperimentAssignmentStrategy.UNIFORM_CHOICE,
                  );
                }}
                disabled={variables.length === 0}
              />
            )}

            {availableStrategieNames.some((strategyName) =>
              [
                ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
                ExperimentAssignmentStrategy.TIME_SLICE,
                ExperimentAssignmentStrategy.TIME_SLICE_V2,
                ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
              ].includes(strategyName),
            ) && (
              <AssignmentStrategyOption
                icon={<ClockOutlined />}
                title={
                  availableStrategieNames.includes(
                    ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                  )
                    ? 'Time Slice (Balanced Shuffle)'
                    : 'Time Slice (Sequential)'
                }
                subtitle="Specify a period of time for each variant."
                selected={[
                  ExperimentAssignmentStrategy.TIME_SLICE,
                  ExperimentAssignmentStrategy.TIME_SLICE_V2,
                  ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
                  ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                ].includes(currentStrategyName)}
                onSelect={() => {
                  if (timeSliceType) {
                    if (
                      timeSliceType ===
                        ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
                      treatments.length >
                        BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS
                    ) {
                      showBalancedShuffleTimeSliceSwitchWarning(timeSliceType);
                      return;
                    }
                    handleSelectStrategy(timeSliceType);
                    setTimeSliceType(timeSliceType);
                    return;
                  }
                  if (
                    availableStrategieNames.includes(
                      ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                    )
                  ) {
                    handleSelectStrategy(
                      ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                    );
                    setTimeSliceType(
                      ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                    );
                    return;
                  }
                  handleSelectStrategy(
                    ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
                  );
                  setTimeSliceType(
                    ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
                  );
                }}
                timeSliceType={timeSliceType}
                extraMenu={
                  availableStrategieNames.includes(
                    ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                  ) && extraTimeSliceMenu
                }
                onSelectExtraItem={(strategy) => {
                  if (
                    strategy ===
                      ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
                    treatments.length >
                      BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS
                  ) {
                    showBalancedShuffleTimeSliceSwitchWarning(strategy);
                    return;
                  }
                  handleSelectStrategy(strategy);
                  setTimeSliceType(strategy);
                }}
                disabled={variables.length === 0}
                selectedStrategy={currentStrategyName}
              />
            )}

            {availableStrategieNames.includes(
              ExperimentAssignmentStrategy.EXPLICIT_GROUP,
            ) && (
              <AssignmentStrategyOption
                icon={<UserGroupOutlined />}
                title="Explicit Group"
                subtitle="Specify a group for each variant."
                selected={
                  currentStrategyName ===
                  ExperimentAssignmentStrategy.EXPLICIT_GROUP
                }
                onSelect={() => {
                  handleSelectStrategy(
                    ExperimentAssignmentStrategy.EXPLICIT_GROUP,
                  );
                }}
                disabled={variables.length === 0}
              />
            )}
          </div>
        </Tooltip>
      )}
      {[
        ExperimentAssignmentStrategy.TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
        ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
      ].includes(currentStrategyName) && (
        <>
          <FullDivider />
          <TimeShuffle onToggle={handleToggleTimeShuffle} />
        </>
      )}
      {currentStrategyName && (
        <>
          <FullDivider />
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <AssignmentStrategyInputCarousel isViewMode={isViewMode} />
            <div
              style={{ height: 40, alignSelf: 'flex-end', marginBottom: 16 }}
            >
              {!isViewMode &&
                [
                  ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
                  ExperimentAssignmentStrategy.UNIFORM_CHOICE,
                ].includes(currentStrategyName) && (
                  <Button
                    style={{ width: 130 }}
                    onClick={() => {
                      scribe.sendEvent(
                        newCreationFlowEvents.clickSplitEvenly.name,
                        {
                          experiment_name: form.getFieldValue(
                            ExpFormField.NAME,
                          ),
                          STATE_NAME:
                            newCreationFlowEvents.clickSplitEvenly.state,
                        },
                      );
                      form.setFieldValue(
                        ExpFormField.STRATEGY.NAME,
                        ExperimentAssignmentStrategy.UNIFORM_CHOICE,
                      );
                      treatments.forEach((treatment) => {
                        form.setFieldValue(
                          ExpFormField.STRATEGY.TREATMENT_VALUE(treatment.id),
                          Math.round(100 / treatments.length),
                        );
                      });
                    }}
                    disabled={
                      currentStrategyName ===
                      ExperimentAssignmentStrategy.UNIFORM_CHOICE
                    }
                  >
                    Split Evenly
                  </Button>
                )}
            </div>
          </div>
        </>
      )}
      {currentStrategyName ===
        ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE &&
        variables.length > 0 && (
          <>
            <FullDivider />
            <AdvancedConfig
              strategy={availableStrategies.find(
                (item) => item.name === currentStrategyName,
              )}
            />
          </>
        )}
      {currentStrategyName === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
        isViewMode &&
        selectedExperiment?.variables?.[0]?.sliceAssignments?.length && (
          <>
            <FullDivider />
            <AllocationCalendar
              title="Allocation"
              startTimestamp={startTime}
              endTimestamp={endTime}
              blockCountEachHour={blockCountEachHour}
              sliceAssignments={
                selectedExperiment.variables[0].sliceAssignments
              }
            />
          </>
        )}
    </>
  );
};

export default AssignmentStrategy;
